import Close from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import MenuIcon from '@mui/icons-material/Menu';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import Modal from '@mui/material/Modal';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import Image from 'next/image';
import { useRouter } from 'next/router';
import React, { useCallback, useMemo, useState } from 'react';
import { RuntimeConfiguration } from '@/configuration';
import { useWallet } from '@/lib';
import { usePayment } from '@/lib/providers/PaymentProvider';
import { StorageService } from '@/lib/services';
import { truncateAddress } from '@/lib/utils/truncateAddress.utils';
import { useOpenClaimModal } from '@/provider/ClaimModalOpenProvider';
import { useContent } from '@/provider/ContentProvider';
import { useMenuDetails } from '@/provider/MenuContext';
import { Images } from '../assets/images';
import ClaimManagementModal from './ClaimManagementModal';
import MenuContainer from './MenuContainer';
import MenuItemContainer from './MenuItemContainer';
import WalletDetails from './WalletDetails';

type Props = {
  headerText?: string;
  buttonName?: string;
  handleMenuClose?: () => void;
};

const Header: React.FC<Props> = ({ headerText, handleMenuClose }) => {
  const theme = useTheme();
  const router = useRouter();
  const isMobile = useMediaQuery(theme.breakpoints.up('sm'));

  const { content } = useContent();
  const { address, networkDetails, balance } = useWallet();
  const { setError } = usePayment();
  const { openClaimModal, setIsDisconnect, setOpenClaimModal, setIsWalletOnly } = useOpenClaimModal();
  const { menuIndex, setMenuIndex } = useMenuDetails();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [copied, setCopied] = useState<string>('');
  const [loggedInAnchorEl, setLoggedInAnchorEl] = useState<null | HTMLElement>(null);
  const [exportWallet, setExportWallet] = useState<boolean>(false);
  const [openDetailModal, setOpenDetailModal] = useState<boolean>(false);
  const [skipClaimModal, setSkipClaimModal] = useState<boolean>(false);

  const open = useMemo(() => {
    return Boolean(anchorEl);
  }, [anchorEl]);

  const modalStyle = useMemo(() => {
    return {
      position: 'absolute',
      bottom: '0',
      width: 'calc(100% - 32px)',
      height: {
        lg: '350px',
        md: '350px',
        sm: '350px',
        xs: open ? 'calc(100% - 46px)' : 'auto'
      },
      bgcolor: theme.palette.secondary.light,
      overflowY: 'auto',
      padding: '16px 16px 30px 16px'
    };
  }, [open, theme]);

  const linkStyle = useMemo(
    () => ({
      textDecoration: 'none',
      fontFamily: theme?.MojitoClaim?.font?.secondary,
      color: theme?.palette?.text?.primary,
      fontSize: '12px',
      fontWeight: 500
    }),
    [theme]
  );

  const onClickMenu = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleClickFaq = useCallback(
    (index?: number, event?: React.MouseEvent<HTMLElement>) => {
      event?.preventDefault();
      setMenuIndex(index ?? 0);
      router.push('/faq');
      handleMenuClose?.();
      handleClose();
    },
    [setMenuIndex, router, handleMenuClose, handleClose]
  );

  const handleClickHome = useCallback(() => {
    router.push('/');
  }, [router]);

  const handleClickClaimMembership = useCallback(
    (index?: number, event?: React.MouseEvent<HTMLElement>) => {
      event?.preventDefault();
      setMenuIndex(index ?? 0);
      router.push('/');
      handleMenuClose?.();
      handleClose();
    },
    [setMenuIndex, router, handleMenuClose, handleClose]
  );

  const onHandleDisconnect = useCallback(async () => {
    setIsDisconnect(true);
    setOpenDetailModal(false);
    handleClose();
  }, [handleClose, setIsDisconnect]);

  const handleClickClaimed = useCallback(
    (index?: number, event?: React.MouseEvent<HTMLElement>) => {
      event?.preventDefault();
      setMenuIndex(index ?? 0);
      router.push('/collectible');
      handleMenuClose?.();
      handleClose();
    },
    [setMenuIndex, router, handleMenuClose, handleClose]
  );

  const onClickConnectWallet = useCallback(() => {
    onHandleDisconnect();
    setOpenClaimModal(true);
    setSkipClaimModal(true);
    setIsWalletOnly(true);
    setError('');
  }, [onHandleDisconnect, setOpenClaimModal, setIsWalletOnly, setError]);

  const handleClickMoreAbout = useCallback(() => {
    window.open('https://kinetek.art/', '_blank', 'noopener,noreferrer');
  }, []);

  const menuOptions = useMemo(() => {
    if (address) {
      if (isMobile)
        return [
          {
            menuName: content?.menuLabel?.loggedIn?.homePage || 'Visit Claim Page',
            menuAction: handleClickClaimMembership
          },
          {
            menuName: content?.menuLabel?.loggedIn?.membershipPage || 'View Membership',
            menuAction: handleClickClaimed
          },
          {
            menuName: content?.menuLabel?.loggedIn?.moreAbout || 'More About Kinetek.art',
            menuAction: handleClickMoreAbout
          },
          {
            menuName: content?.menuLabel?.loggedIn?.faq || 'FAQs',
            menuAction: handleClickFaq
          }
        ];
      else
        return [
          {
            menuName: content?.menuLabel?.loggedIn?.homePage || 'Visit Claim Page',
            menuAction: handleClickClaimMembership
          },
          {
            menuName: content?.menuLabel?.loggedIn?.membershipPage || 'View Membership',
            menuAction: handleClickClaimed
          },
          {
            menuName: content?.menuLabel?.loggedIn?.moreAbout || 'More About Kinetek.art',
            menuAction: handleClickMoreAbout
          },
          {
            menuName: content?.menuLabel?.loggedIn?.faq || 'FAQs',
            menuAction: handleClickFaq
          },
          {
            menuName: content?.menuLabel?.loggedIn?.logout || 'Log Out',
            menuAction: onHandleDisconnect
          }
        ];
    } else {
      return [
        {
          menuName: content?.menuLabel?.loggedOut?.homePage || 'Claim Your Membership',
          menuAction: handleClickClaimMembership
        },
        {
          menuName: content?.menuLabel?.loggedIn?.moreAbout || 'More About Kinetek.art',
          menuAction: handleClickMoreAbout
        },
        {
          menuName: content?.menuLabel?.loggedOut?.faq || 'FAQs',
          menuAction: handleClickFaq
        }
      ];
    }
  }, [
    isMobile,
    onHandleDisconnect,
    address,
    content.menuLabel,
    handleClickFaq,
    handleClickClaimMembership,
    handleClickClaimed,
    handleClickMoreAbout
  ]);

  const handleClickLoggedIn = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      if (isMobile) {
        setLoggedInAnchorEl(event.currentTarget);
      }
      setOpenDetailModal(true);
    },
    [isMobile]
  );

  const handleCloseDetailModal = useCallback(() => {
    setOpenDetailModal(false);
  }, []);

  const onClickViewWallet = useCallback(() => {
    setMenuIndex(undefined);
    handleClose();
    handleCloseDetailModal();
    router.push('/wallets');
  }, [setMenuIndex, handleClose, handleCloseDetailModal, router]);

  const onClickCopy = useCallback((data: string) => {
    navigator.clipboard
      .writeText(data)
      .then(() => {
        setCopied(data);
        setTimeout(() => {
          setCopied('close');
        }, 1000);
      })
      .catch(() => {
        setCopied('close');
      });
  }, []);

  const onCloseModal = useCallback(() => {
    setOpenClaimModal(false);
    setIsWalletOnly(false);
    setSkipClaimModal(false);
  }, [setOpenClaimModal, setIsWalletOnly]);

  const handleSuccessRedirection = useCallback(
    (hash?: string) => {
      setMenuIndex(0);
      router.push('/collectible');
      setOpenClaimModal(false);
      setIsWalletOnly(false);
      StorageService.transactionHash.setValue(hash);
      StorageService.requiredGas.clear();
    },
    [setMenuIndex, router, setOpenClaimModal, setIsWalletOnly]
  );

  const currency = useMemo(() => {
    if (networkDetails?.chainID === 80001 || networkDetails?.chainID === 137) {
      return {
        currencyName: 'MATIC',
        currencyIcon: Images.MATIC.default.src
      };
    }

    return {
      currencyName: 'ETH',
      currencyIcon: Images.ETHICON.default.src
    };
  }, [networkDetails?.chainID]);

  // const skipClaimModal = useMemo(() => {
  //   return router?.asPath?.includes('faq') || router?.asPath?.includes('collectible');
  // }, [router]);

  const handleExportKey = useCallback(() => {
    setExportWallet(true);
    setOpenDetailModal(false);
  }, []);

  return (
    <>
      <Box
        sx={{
          background: theme?.ClaimTemplate?.Header?.background,
          display: 'flex',
          justifyContent: 'space-between',
          position: 'sticky',
          top: 0,
          zIndex: openClaimModal ? 0 : 500,
          alignItems: 'center',
          // borderBottom: `1px solid ${theme?.ClaimTemplate?.Header?.borderColor}`,
          boxShadow: '0px -1px 0px 0px #1A1A1A inset',
          height: '72px'
        }}
      >
        <Box
          sx={{
            padding: {
              lg: '4.5px 24px',
              xs: '4.5px 12px'
            }
          }}
        >
          {headerText ? (
            <Box
              sx={{
                cursor: 'pointer'
              }}
              display="flex"
              flexDirection="row"
              alignItems="center"
            >
              {/* <ArrowBackIosNewOutlined
                sx={{
                  width: '20px',
                  height: '20px',
                  cursor: 'pointer',
                  color: theme?.palette?.text?.primary
                }}
                onClick={() => router.push('/')}
              /> */}
              <Typography
                onClick={handleClickHome}
                sx={{
                  fontFamily: theme?.MojitoClaim?.font?.primary,
                  fontSize: isMobile ? '32px' : '30px',
                  lineHeight: isMobile ? '38px' : '120%',
                  fontStyle: 'normal',
                  fontWeight: 500,
                  textAlign: 'left',
                  color: theme?.palette?.text?.primary
                }}
              >
                {headerText}
              </Typography>
            </Box>
          ) : (
            <Image
              src={isMobile && content?.mobileLogoURL ? content?.mobileLogoURL : content.logoURL}
              onClick={() => handleClickClaimMembership()}
              style={{
                width: 'auto',
                height: 'auto',
                objectFit: 'contain',
                maxHeight: '55px',
                cursor: 'pointer'
              }}
              alt="logo"
            />
          )}
        </Box>
        <Stack
          direction={'row'}
          spacing={2}
          sx={{
            height: '40px',
            padding: {
              lg: '16px 24px',
              xs: '16px 12px'
            }
          }}
        >
          {!address ? (
            <>
              <Button
                sx={{
                  width: '100%',
                  marginTop: '16px',
                  background: theme.palette?.background?.paper,
                  color: theme.palette?.primary?.main,
                  border: `1px solid ${theme.palette?.primary?.main}`,
                  fontSize: {
                    xs: '12px',
                    sm: '16px',
                    md: '16px',
                    lg: '16px'
                  },
                  textTransform: 'none'
                }}
                onClick={onClickConnectWallet}
              >
                Connect or create wallet
              </Button>
              <Typography
                onClick={onClickMenu}
                sx={{
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <MenuIcon
                  sx={{
                    cursor: 'pointer',
                    width: '28px',
                    height: '28px',
                    padding: '6px 0',
                    color: theme?.palette?.text?.primary
                  }}
                />
              </Typography>
            </>
          ) : (
            <>
              <Typography
                sx={{
                  border: `1px solid ${theme?.palette?.primary?.dark}`,
                  borderRadius: '4px',
                  padding: {
                    xs: '4px 8px',
                    sm: '4px 8px',
                    md: '8px 16px',
                    lg: '8px 16px'
                  },
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                  fontFamily: theme?.MojitoClaim?.font?.secondary
                }}
                onClick={handleClickLoggedIn}
              >
                <Typography
                  sx={{
                    backgroundColor: theme?.palette?.success?.light,
                    width: '6px',
                    height: '6px',
                    borderRadius: '50%',
                    marginRight: '9px'
                  }}
                />
                <Typography
                  sx={{
                    color: theme?.palette?.text?.primary,
                    marginRight: '9px',
                    fontSize: {
                      xs: '12px',
                      sm: '12px',
                      md: '16px',
                      lg: '16px'
                    }
                  }}
                >
                  {router?.asPath?.includes('wallet') && address ? truncateAddress(address ?? '') : 'Connected'}
                </Typography>
                <KeyboardArrowDownIcon
                  sx={{
                    color: theme?.palette?.text?.primary
                  }}
                />
              </Typography>
              <Typography
                onClick={onClickMenu}
                sx={{
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <MenuIcon
                  sx={{
                    cursor: 'pointer',
                    width: '28px',
                    height: '28px',
                    padding: '6px 0',
                    color: theme?.ClaimTemplate?.Header?.color
                  }}
                />
              </Typography>
            </>
          )}
        </Stack>
        <MenuContainer
          menuOptions={menuOptions}
          isMobile={isMobile}
          open={open}
          anchorEl={anchorEl}
          activeIndex={menuIndex}
          handleMenuClose={handleClose}
        />
        <Menu
          open={isMobile && openDetailModal}
          anchorEl={loggedInAnchorEl}
          onClose={handleCloseDetailModal}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right'
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right'
          }}
        >
          <Box
            sx={{
              background: theme.palette.secondary.light
            }}
            padding="8px 16px"
            width="375px"
          >
            <WalletDetails
              currencyIcon={currency.currencyIcon ?? Images?.ETHICON}
              walletBalance={balance?.native ?? 0}
              walletAddress={address ?? ''}
              currency={currency.currencyName ?? 'ETH'}
              copied={copied}
              isMobile={false}
              isThirdWeb={false}
              handleExportKey={handleExportKey}
              onClickLogout={onHandleDisconnect}
              onClickCopy={onClickCopy}
              onClickViewWallet={onClickViewWallet}
            />
          </Box>
        </Menu>
        <Modal open={!isMobile && openDetailModal} onClose={handleCloseDetailModal}>
          <Box sx={modalStyle}>
            <Box
              position="fixed"
              sx={{
                right: '17px'
              }}
            >
              <Close
                sx={{
                  cursor: 'pointer',
                  color: theme.palette.background.default,
                  height: '24px',
                  weight: '24px'
                }}
                onClick={handleCloseDetailModal}
              />
            </Box>
            <Box padding="40px 16px">
              <WalletDetails
                currencyIcon={currency.currencyIcon ?? Images?.ETHICON}
                walletBalance={balance?.native ?? 0}
                walletAddress={address ?? ''}
                currency={currency.currencyName ?? 'ETH'}
                copied={copied}
                isMobile={true}
                isThirdWeb={false}
                handleExportKey={handleExportKey}
                onClickLogout={onHandleDisconnect}
                onClickCopy={onClickCopy}
                onClickViewWallet={onClickViewWallet}
              />
            </Box>
          </Box>
        </Modal>
        <Modal open={!isMobile && open} onClose={handleClose}>
          <Box sx={modalStyle}>
            <Box display="flex" justifyContent="flex-end" padding="8px">
              <Close
                sx={{
                  cursor: 'pointer',
                  color: theme.palette.background.default
                }}
                onClick={handleClose}
              />
            </Box>
            <MenuItemContainer menuOptions={menuOptions} isMobile={isMobile} activeIndex={menuIndex} />
            <Stack
              direction={{
                xs: 'row'
              }}
              spacing={{
                xs: 2
              }}
              position="fixed"
              bottom={'40px'}
              left={'32px'}
            >
              <a rel="noreferrer" href="/terms-and-conditions" target="_blank" style={linkStyle}>
                Terms & Conditions
              </a>
              <a rel="noreferrer" href="/privacy-policy" target="_blank" style={linkStyle}>
                Privacy Policy
              </a>
              {content.footer.contactUsURL && (
                <a rel="noreferrer" href={content.footer.contactUsURL} target="_blank" style={linkStyle}>
                  Contact Us
                </a>
              )}
            </Stack>
          </Box>
        </Modal>
        <ClaimManagementModal
          open={openClaimModal}
          skipClaimModal={skipClaimModal}
          onCloseModal={onCloseModal}
          config={{
            crossmintApiKey: RuntimeConfiguration.CROSSMINT_API ?? '',
            crossmintEnv: RuntimeConfiguration?.CROSSMINT_ENV ?? '',
            orgId: RuntimeConfiguration.ORG_ID ?? '',
            chainId: Number(RuntimeConfiguration.CHAIN_ID) ?? 1,
            paperClientId: RuntimeConfiguration.PAPER_CLIENT_ID ?? '',
            paperNetworkName: RuntimeConfiguration.NETWORK_NAME ?? ''
          }}
          claimItemId="22942e6d-4e01-4089-a9da-c1b8d44ea51b"
          claimCode={content.claimCode}
          saleType="BuyNow"
          link={{
            termsUrl: '/terms-and-conditions',
            logoUrl: content.claimLogoURL ?? content.logoURL,
            privacyUrl: '/privacy-policy',
            additionalTermsUrl: content.footer?.additionalTermsAndConditionsURL
          }}
          content={{ ...content.popupContent, walletLogo: '/kinetek.png' }}
          tokenGatingConfig={{
            groupId: '',
            ruleId: ''
          }}
          isClaimWithGas={true}
          tokenName="Mercedes-Benz NXT"
          onSuccess={handleSuccessRedirection}
        />
      </Box>
    </>
  );
};

export default Header;
